import { z } from "zod";
import { CommodityEnumSchema, ProcessEnumSchema, ProcessSpecificationEnumSchema, SegmentEnumSchema } from "@/types/contract";

export const generalEnumerationSchema = z.object({
  channels: z.optional(z.array(z.string())),
  id: z.string().or(z.number()),
  name: z.string(),
  validFrom: z.optional(z.number()),
  validTo: z.optional(z.nullable(z.number())),
  value: z.string(),
});

export const priceListSchema = z.object({
  channels: z.optional(z.array(z.string())),
  id: z.string().or(z.number()),
  name: z.string(),
  validFrom: z.optional(z.number()),
  validTo: z.optional(z.nullable(z.number())),
  documentUrl: z.string(),
});

export const gasEquipmentSchema = z.object({
  id: z.number(),
  name: z.nullable(z.string()),
  value: z.optional(z.string()),
  validTo: z.optional(z.nullable(z.number())),
  validFrom: z.optional(z.nullable(z.number())),
  distributorId: z.string(),
  customerType: z.nullable(z.string()),
});

export const powerEquipmentSchema = z.object({
  id: z.number(),
  name: z.nullable(z.string()),
  value: z.optional(z.string()),
  validTo: z.optional(z.nullable(z.number())),
  validFrom: z.optional(z.nullable(z.number())),
  distributorId: z.string().optional(),
});

export const agreementSchema = z.object({
  channels: z.array(z.string()),
  id: z.string(),
  name: z.string(),
  validFrom: z.number(),
  validTo: z.number(),
  value: z.object({
    desc: z.string(),
    perex: z.string(),
    type: z.string(),
  }),
});

export const attachmentSchema = z.object({
  id: z.string().or(z.number()),
  name: z.string(),
  descriptions: z.string().optional().nullable(),
  nameForCustomers: z.string().nullable(),
  value: z.object({
    type: z.string(),
    commodity: CommodityEnumSchema,
    processId: ProcessEnumSchema,
    required: z.boolean(),
    processReasonId: z.string().nullable(),
    customerType: z.string().nullable(),
  }),
});

export const supplierSchema = z.object({
  channels: z.array(z.string()),
  id: z.string(),
  name: z.string(),
  validFrom: z.number(),
  validTo: z.number(),
  value: z.object({
    commodity: CommodityEnumSchema,
    externalId: z.string(),
  }),
});

export const circuitBreakersSchema = z.object({
  channels: z.optional(z.array(z.string())),
  id: z.string().or(z.number()),
  circuitBreakerPhase: z.number(),
  circuitBreakerValue: z.number(),
  nameForCustomers: z.string(),
});

export const measurementTypeSchema = z.object({
  id: z.string().or(z.number()),
  measurementType: z.string(),
  commodityId: z.number(),
  validFrom: z.number(),
  validTo: z.number(),
  nameForCustomers: z.string().nullable(),
  name: z.string().optional(),
});

// TODO do we need all these parameters
export const productSchema = z.object({
  bundleServices: z.array(z.string()),
  channels: z.array(z.string()),
  fictive: z.boolean(),
  id: z.string(),
  name: z.string(),
  onlineRequired: z.boolean(),
  processes: z.array(ProcessEnumSchema),
  regions: z.array(z.string()),
  segment: SegmentEnumSchema,
  validFrom: z.number(),
  validTo: z.number(),
  value: z.object({
    relevance: z.string().nullable().default(null),
    commodity: CommodityEnumSchema,
    contractTemplateId: z.string(),
    customerType: z.string(),
    disId: z.string(),
    distributors: z.array(
      z.object({
        distributor: z.string(),
        priceList: z.string(),
        priceListUrl: z.string(),
      })
    ),
    ldsup: z.boolean(),
    otherTemplateId: z.array(z.string()),
    otherTemplates: z.array(
      z.object({
        name: z.string(),
        otherTemplateId: z.string(),
        otherTemplateUrl: z.string(),
      })
    ),
    pricelistValidFrom: z.number(),
    pricelistValidMonth: z.number(),
    pricelistValidTo: z.number(),
    productDescription: z.nullable(z.string()),
    productId: z.nullable(z.string()),
    supplyProducts: z.array(z.string()),
    priceGreen: z.number().nullable(),
    forbiddenOverflow: z.boolean(),
  }),
});

export const tariffSchema = z.object({
  channels: z.array(z.string()),
  databaseId: z.number(),
  id: z.string(),
  name: z.string(),
  nameForCustomers: z.string().nullable(),
  validFrom: z.number(),
  validTo: z.number(),
  legacy: z.boolean().nullable().optional(),
  tariffType: z.string().nullable().optional(),
  value: z.object({
    customerType: z.string(),
    processes: z.array(ProcessEnumSchema),
    supplyProduct: z.string(),
  }),
});

export const commoditySchema = z.object({
  channels: z.array(z.string()),
  id: z.string().or(z.number()),
  name: z.string(),
  value: CommodityEnumSchema,
});

export const processSchema = z.object({
  channels: z.array(z.string()),
  id: z.string().or(z.number()),
  name: z.string(),
  nameForCustomers: z.string().nullable().default(null),
  value: ProcessEnumSchema,
  processReasons: z.array(z.string()),
});

export const countrySchema = generalEnumerationSchema.merge(
  z.object({
    telephoneCode: z.string(),
  })
);

export const bonusSchema = z.object({
  id: z.number(),
  name: z.nullable(z.string()),
  feSubject: z
    .string()
    .nullable()
    .transform(value => value ?? ""),
  feAgreementSubject: z
    .string()
    .nullable()
    .transform(value => value ?? ""),
  feAgreementContent: z
    .string()
    .nullable()
    .transform(value => value ?? ""),
  feInformationSubject: z
    .string()
    .nullable()
    .transform(value => value ?? ""),
  feInformationContent: z
    .string()
    .nullable()
    .transform(value => value ?? ""),
  bonusTypeId: z.number().nullable(),
});

export const supplyPointUsagesSchema = z.object({
  id: z.string(),
  name: z.string(),
  value: z.object({
    commodity: z.string(),
    consumptionAb: z.number(),
    consumptionBis: z.number(),
    slpClass: z.nullable(z.string()),
    slpcat: z.string(),
    tdS01: z.boolean(),
    tdS02: z.boolean(),
    tdS03: z.boolean(),
    tdS04: z.boolean(),
    tdVyuzOm: z.string(),
  }),
});

export const serviceAttributesSchema = z.object({
  id: z.string(),
  name: z.string(),
  value: z.string(),
  bundleServiceId: z.string(),
  attribute: z.string(),
});

export const serviceProductsSchema = z.object({
  id: z.string(),
  name: z.string(),
  value: z.string(),
  bundleServiceId: z.string(),
});

export const terminationTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  terminationType: z.string(),
  nameForCustomers: z.string().nullable(),
});

export const determinationReasonSchema = z.object({
  id: z.number(),
  name: z.string(),
  determinationReason: z.string(),
  nameForCustomers: z.string().nullable(),
});

export const processReasonSchema = z.object({
  id: z.number(),
  name: z.string(),
  processReason: z.string(),
  nameForCustomers: z.string().nullable(),
});

export const processSpecificationSchema = z.object({
  id: z.number(),
  value: ProcessSpecificationEnumSchema,
});

export const changeOfCustomerReasonSchema = z.object({
  id: z.number(),
  name: z.string(),
  nameForCustomers: z.string().nullable(),
  value: z.string(),
  processReasons: z.string(),
});

export const distributionAreasSchema = z.object({
  id: z.nullable(z.number()),
  validTo: z.nullable(z.number()),
  validFrom: z.nullable(z.number()),
  channels: z.array(z.string()),
  name: z.nullable(z.string()),
  value: z.nullable(z.string()),
  districtCode: z.nullable(z.string()),
  districtName: z.nullable(z.string()),
  commodity: CommodityEnumSchema,
});

export const distributionGridsSchema = z.object({
  id: z.nullable(z.string()),
  validTo: z.nullable(z.number()),
  validFrom: z.nullable(z.number()),
  channels: z.array(z.string()),
  name: z.nullable(z.string()),
  value: z.object({
    supGridId: z.nullable(z.string()),
    distributorId: z.nullable(z.string()),
    podPrefix: z.nullable(z.string()),
    gridLevelType: z.nullable(z.string()),
  }),
});

export const plannedConsumptionsSchema = z.object({
  name: z.nullable(z.string()),
  minM3: z.nullable(z.number()),
  maxM3: z.nullable(z.number()),
  minMwh: z.nullable(z.number()),
  maxMwh: z.nullable(z.number()),
  circuitBreakerPhase: z.nullable(z.number()),
  circuitBreakerValueMax: z.nullable(z.number()),
  circuitBreakerValueMin: z.nullable(z.number()),
  tariffId: z.nullable(z.number()),
  segment: SegmentEnumSchema,
});

export const bonusTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
});
